import {
  Connection,
  VersionedTransaction,
  PublicKey,
  ParsedTransactionWithMeta,
} from '@solana/web3.js';

function isDuplicateProcessedError(e: any) {
  const msg = String(e?.message ?? e);
  return msg.includes('already been processed');
}

function xerr(message: string, extra?: Record<string, any>) {
  const err = new Error(message);
  if (extra) Object.assign(err as any, extra);
  return err;
}

export async function sendAndIdempotentlyConfirm(
  connection: Connection,
  tx: VersionedTransaction,
  feePayer: PublicKey,
  opts: { commitment?: 'confirmed' | 'finalized' } = {}
): Promise<{ signature: string; tx: ParsedTransactionWithMeta | null }> {
  const commitment = opts.commitment ?? 'confirmed';

  if (!tx.signatures?.length) throw xerr('UNSIGNED_TX');

  try {
    const sig = await connection.sendRawTransaction(tx.serialize(), {
      skipPreflight: false,
      maxRetries: 2,
    });
    const latest = await connection.getLatestBlockhash();
    await connection.confirmTransaction({ signature: sig, ...latest }, commitment);
    const full =
      (await connection.getTransaction(sig, {
        maxSupportedTransactionVersion: 0,
        commitment,
      })) ?? null;
    return { signature: sig, tx: full };
  } catch (e: any) {
    if (typeof e?.getLogs === 'function') {
      try { (e as any).__rpcLogs = await e.getLogs(); } catch {}
    }
    if (isDuplicateProcessedError(e)) {
      const sigs = await connection.getSignaturesForAddress(feePayer, { limit: 10 });
      if (sigs[0]?.signature) {
        const recovered = sigs[0].signature;
        const full =
          (await connection.getTransaction(recovered, {
            maxSupportedTransactionVersion: 0,
            commitment,
          })) ?? null;
        return { signature: recovered, tx: full };
      }
      throw xerr('DUPLICATE_NO_RECOVERY', { __duplicateProcessed: true, original: e });
    }
    throw e;
  }
}

export function isDuplicateSuccess(e: any): boolean {
  return !!(e && (e.__duplicateProcessed || isDuplicateProcessedError(e)));
}
